import { ReviewSortEnum } from '@iheartjane/dm-sdk';
import { useEffect, useState } from 'react';
import { useFetcher } from 'react-router';

import type { LoaderResponse } from '@jane/bloom/routes/_menu.products.$productId.$slug/types';
import { ReviewIntent } from '@jane/bloom/routes/products.$productId.$slug.reviews/types';
import { buildFormData } from '@jane/bloom/utils/formData';
import type {
  ProductReviewViewModel,
  ProductReviewsPaginationViewModel,
} from '@jane/bloom/viewModels/productReviews';

type Args = {
  initialPagination: ProductReviewsPaginationViewModel;
  initialReviews: ProductReviewViewModel[];
  productId: number;
  productSlug?: string;
};

type ReturnType = {
  canLoadMoreReviews: boolean;
  currentSortOrder: ReviewSortEnum;
  displayableReviews: ProductReviewViewModel[];
  handleLoadMore: () => void;
  handleSort: (nextOrder: ReviewSortEnum) => void;
  isLoading: boolean;
  totalCount: number;
  upvote: (reviewId: number) => void;
};

const dedupeReviewsById = (reviews: ProductReviewViewModel[]) =>
  Array.from(new Map(reviews.map((review) => [review.id, review])).values());

export const useProductReviewsFetcher = ({
  initialPagination,
  initialReviews,
  productId,
  productSlug,
}: Args): ReturnType => {
  const fetcher = useFetcher<LoaderResponse>();
  const [page, setPage] = useState(initialPagination.currentPage);
  const [sortOrder, setSortOrder] = useState<ReviewSortEnum>(
    ReviewSortEnum.Recent
  );
  const [displayableReviews, setDisplayableReviews] = useState(initialReviews);
  const [pagination, setPagination] = useState(initialPagination);

  const isLoading = fetcher.state !== 'idle';
  const canLoadMoreReviews = pagination.hasNextPage && !isLoading;

  const buildReviewsUrl = ({
    page,
    sort,
  }: {
    page: number;
    sort: ReviewSortEnum;
  }) => {
    const params = new URLSearchParams({
      reviewsPage: `${page}`,
      reviewsSort: sort,
    });

    return `/products/${productId}/${productSlug ?? ''}?${params.toString()}`;
  };

  const handleSort = (newSortOrder: ReviewSortEnum) => {
    setSortOrder(newSortOrder);
    fetcher.load(buildReviewsUrl({ page: 1, sort: newSortOrder }));
  };

  const handleLoadMore = () => {
    if (!canLoadMoreReviews) {
      return;
    }
    fetcher.load(buildReviewsUrl({ page: page + 1, sort: sortOrder }));
  };

  useEffect(() => {
    if (!fetcher.data || !('productReviews' in fetcher.data)) {
      return;
    }

    const { pagination: nextPagination, reviews } = fetcher.data.productReviews;

    setPagination(nextPagination);
    setPage(nextPagination.currentPage);

    if (nextPagination.currentPage === 1) {
      setDisplayableReviews(reviews);
    } else {
      setDisplayableReviews((current) =>
        dedupeReviewsById([...current, ...reviews])
      );
    }
  }, [fetcher.data]);

  const upvote = (reviewId: number) => {
    fetcher.submit(
      buildFormData({
        intent: ReviewIntent.Upvote,
        page,
        productId,
        reviewId,
        sortOrder,
      }),
      {
        action: `/products/${productId}/${productSlug}/reviews`,
        method: 'post',
      }
    );
  };

  return {
    canLoadMoreReviews,
    currentSortOrder: sortOrder,
    displayableReviews,
    handleLoadMore,
    handleSort,
    isLoading,
    totalCount: pagination.totalCount,
    upvote,
  };
};
